import React, { useState } from 'react';
import objStr from 'obj-str';
import useTranslation from 'next-translate/useTranslation';
import navigationService from '@/services/navigation-service';
import { Portal } from '@/components/Portal';
import { FullPageScrollPane } from '@/components/FullPageScrollPane';
import styles from './CategoryNavigation.module.scss';
import type * as Types from '@/pages/mijn-carel-lurvink/catalogus/stores/types';

interface ICategoryNavigation {
  categories: Types.Category[];
  isOpen?: boolean;
  onClose: AppCallbackFn;
  onMenuClose: AppCallbackFn;
}
export const CategoryNavigation = ({ categories: categoriesFromProps, isOpen = false, onClose, onMenuClose }: ICategoryNavigation) => {
  const { t } = useTranslation();
  navigationService.setCategories(categoriesFromProps);
  const rootCategories = navigationService.getRootCategories();
  const [currentCategory, setCurrentCategory] = useState<Types.Category>();
  const [categories, setCategories] = useState<Types.Category[]>(navigationService.getRootCategories());
  const isRoot = !currentCategory?.parentCode && !currentCategory?.name;

  const onCategoryChange = (code: string) => {
    if (code === '-1') {
      setCurrentCategory(undefined);
      setCategories(navigationService.getRootCategories());
      return;
    }

    const nextCurrentCategory = navigationService.getCategoryByCode(code);
    const nextCurrentCategories = navigationService.getChildCategoriesByChildCodes(nextCurrentCategory.childCodes);
    setCurrentCategory(nextCurrentCategory);
    setCategories(nextCurrentCategories);
  };

  const handleClose = () => {
    setCurrentCategory(undefined);
    setCategories(navigationService.getRootCategories());
    onClose();
  };

  const classNames = objStr({
    [styles.CategoryNavigation]: true,
    [styles.isOpen]: isOpen
  });
  return (
    <Portal>
      <FullPageScrollPane trueFullScreen isOpen={isOpen} className="bg-white">
        <ul className={classNames} data-testid="catalog:category-navigation">
          <li className={`${styles.categoryHeader} d-flex justify-content-between align-items-center px-3`}>
            {currentCategory && (
              <a role="button" className="d-inline-block" onClick={() => onCategoryChange(currentCategory.parentCode ?? '-1')}>
                <span className="fa fa-long-arrow-left text-gray-900 mr-1"></span>
                <span className={`${styles.categoryName} text-gray-900`}>{currentCategory?.name || ''}</span>
              </a>
            )}
            {!currentCategory && (
              <a role="button" className="d-inline-block" onClick={handleClose}>
                <span className="fa fa-long-arrow-left text-gray-900 mr-1"></span>
                <span className={`${styles.categoryName} text-gray-900`}>{t('common:main-navigation.Products')}</span>
              </a>
            )}

            <button type="button" className="btn btn-link pr-0" onClick={onMenuClose}>
              <span className="fa fa-times text-lg text-theme mr-1"></span>
              <span className="text-sans-serif text-gray-900 text-nowrap">{t('common:button.close')}</span>
            </button>
          </li>

          {isRoot && (
            <>
              {rootCategories.map((child) => {
                const isActive = currentCategory?.code === child.code;
                return (
                  <li key={child.code}>
                    <a
                      role="button"
                      onClick={() => onCategoryChange(child.code)}
                      className={`${styles.categoryLink} ${isActive ? styles.categoryLinkActive : ''}`}
                      data-testid={`cat-${child.code}`}
                    >
                      <span>{child.name}</span>
                      <span className="cl-long-arrow-right"></span>
                    </a>
                  </li>
                );
              })}
            </>
          )}

          {!isRoot && (
            <>
              {categories.map((child) => {
                const hasChildren = child.childCodes?.length > 0;

                return (
                  <li key={child.code}>
                    {hasChildren ? (
                      <a
                        role="button"
                        onClick={() => onCategoryChange(child.code)}
                        className={styles.categoryLink}
                        data-testid={`cat-${child.code}`}
                      >
                        {child.name}
                        <span className="cl-long-arrow-right"></span>
                      </a>
                    ) : (
                      <a href={child.urlSet.web} className={styles.categoryLink} data-testid={`cat-${child.code}`}>
                        {child.name}
                      </a>
                    )}
                  </li>
                );
              })}
            </>
          )}
        </ul>
      </FullPageScrollPane>
    </Portal>
  );
};
